import { ProgramPlanBadge as DesignSystemProgramPlanBadge, Tooltip } from '@quickmd-llc/design-system'
import { getProgramPlanBadgeLabel } from '@quickmd-llc/frontend-utils'
import { getProgramPlanExpiryLabel } from './getProgramPlanExpiryLabel'
import { useProgramPlanPackage } from 'hooks/useProgramPlanPackage'

type Props = {
  className?: string
  /** The patient whose plans to read. `null` while the chart is still loading. */
  patientId: string | null | undefined
}

/**
 * `Quit 7-OH Plan: 3 visits remaining` — the chip on the patient chart header
 * that tells a clinician this patient is on the Kratom / 7-OH support plan and
 * what is left of it (QMD-4145). On hover it surfaces the plan's expiry date so
 * the clinician can see how long that coverage lasts without leaving the chart.
 *
 * The chip is `@quickmd-llc/design-system`'s and renders nothing when the patient
 * holds no usable plan, so this can be dropped into the tag row unconditionally
 * rather than repeating the guard there. What this wrapper adds is the fetch, the
 * kill switch, the expiry tooltip, and the `tailwind-scoped` span — all provider's.
 *
 * The span is not optional and not cosmetic. `src/index.css` nests all three
 * `@tailwind` directives inside `.tailwind-scoped`, so every utility is a
 * descendant selector — a design-system component rendered on a legacy SCSS page
 * with no scoped ancestor gets *zero* styling and reads as raw dark text. The
 * patient chart is exactly such a page, and the tooltip's own popup (which the DS
 * `Tooltip` renders inline, not through a portal) is Tailwind-styled too, so it
 * must live inside the span as well. Scoping here rather than at the call site is
 * what stops the next placement getting it wrong; see `ProgramVisitBadge`, which
 * carries the same wrapper for the same reason.
 *
 * The tooltip wraps the chip only when the chip is actually drawn *and* there is
 * an expiry to show: `getProgramPlanBadgeLabel` is the DS chip's own render gate,
 * so a spent or lapsed plan draws nothing, and a tooltip whose trigger drew
 * nothing is a hover target hanging over empty space.
 */
export function ProgramPlanBadge({ className, patientId }: Props) {
  const programPackage = useProgramPlanPackage(patientId)
  const expiryLabel = getProgramPlanExpiryLabel(programPackage)

  const badge = <DesignSystemProgramPlanBadge className={className} programPackage={programPackage} />

  const chip =
    getProgramPlanBadgeLabel(programPackage) && expiryLabel ? <Tooltip content={expiryLabel}>{badge}</Tooltip> : badge

  return <span className='tailwind-scoped tailwind-scoped-contents'>{chip}</span>
}
